import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { formatRegistrationDate, formatRegistrationTime, getRegistrationDateLocale } from '@application/utilities/registration-date-time';
import { DEFAULT_WELCOME_TEMPLATE, replaceWelcomePlaceholders } from '@application/utilities/welcome-text-placeholders';
import { validateBackgroundImages } from '@application/helpers/background-image-helper';
import { isVideo } from '@application/helpers/video-detection-helper';
import { Alert, Box, Typography, useTheme } from '@mui/material';
import TextEditor from '@ui/components/TextEditor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ThankYouScreenContent from '../ThankYouScreenContent';
import useStartScreenStyles from './useStartScreenContentStyles';
// Default thumbnails for initial display
const defaultThumbnails = [
    { src: '/assets/images/thumbnail/thumbnail_1.png', isSelected: true },
    { src: '/assets/images/thumbnail/thumbnail_2.png', isSelected: false },
    { src: '/assets/images/thumbnail/thumbnail_2.png', isSelected: false },
    { src: '/assets/images/thumbnail/thumbnail_3.png', isSelected: false },
    { src: '/assets/images/thumbnail/thumbnail_4.png', isSelected: false }
];
const StartScreenContent = ({ watch, onBackgroundImagesChange, backgroundImages = [], entryMode, translationLocale }) => {
    const { t: tRaw, i18n } = useTranslation();
    const t = translationLocale ? i18n.getFixedT(translationLocale) : tRaw;
    const styles = useStartScreenStyles();
    const [validationErrors, setValidationErrors] = useState([]);
    const [imageUrls, setImageUrls] = useState({});
    const theme = useTheme();
    const eventDuration = watch('eventDuration');
    const startTime = watch('startTime');
    const endTime = watch('endTime');
    const location = watch('location');
    const welcomeText = watch('welcomeText');
    const previewLocale = useMemo(() => getRegistrationDateLocale(undefined, undefined, translationLocale ?? i18n.language ?? 'en'), [translationLocale, i18n.language]);
    const resolvedWelcomeText = useMemo(() => {
        const dateRange = formatRegistrationDate({ startDate: eventDuration?.startDate, endDate: eventDuration?.endDate }, previewLocale);
        const timeRange = formatRegistrationTime({
            startDate: eventDuration?.startDate,
            endDate: eventDuration?.endDate,
            startTime: startTime ?? undefined,
            endTime: endTime ?? undefined
        }, previewLocale);
        const template = welcomeText?.trim() || DEFAULT_WELCOME_TEMPLATE;
        return replaceWelcomePlaceholders(template, {
            dateRange,
            timeRange,
            location: location ?? ''
        });
    }, [
        welcomeText,
        eventDuration?.startDate,
        eventDuration?.endDate,
        startTime,
        endTime,
        location,
        previewLocale
    ]);
    // Create object URLs for images and clean up old ones
    useEffect(() => {
        const newUrls = {};
        backgroundImages.forEach((image, index) => {
            if (image.file) {
                // Uploaded image
                const key = `${image.file.name}-${image.file.size}-${image.file.lastModified}`;
                if (!imageUrls[key]) {
                    const newUrl = URL.createObjectURL(image.file);
                    newUrls[key] = newUrl;
                }
                else {
                    newUrls[key] = imageUrls[key];
                }
            }
            else if (image.url) {
                // Backend image
                newUrls[image.url] = image.url;
            }
        });
        // Clean up old URLs that are no longer needed (only for object URLs)
        Object.keys(imageUrls).forEach((key) => {
            if (!Object.keys(newUrls).includes(key) && imageUrls[key].startsWith('blob:')) {
                URL.revokeObjectURL(imageUrls[key]);
            }
        });
        setImageUrls(newUrls);
    }, [backgroundImages]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            Object.values(imageUrls).forEach((url) => {
                if (url.startsWith('blob:'))
                    URL.revokeObjectURL(url);
            });
        };
    }, []);
    const handleFileUpload = useCallback((event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0)
            return;
        // Validate files
        const errors = validateBackgroundImages(files);
        setValidationErrors(errors);
        if (errors.length > 0) {
            return;
        }
        // Create new background image selections
        const newImages = files.map((file) => ({
            file,
            isSelected: false
        }));
        // If this is the first image, make it selected
        if (backgroundImages.length === 0 && newImages.length > 0) {
            newImages[0].isSelected = true;
        }
        const updatedImages = [...backgroundImages, ...newImages];
        onBackgroundImagesChange?.(updatedImages);
        // Clear the input value to allow re-uploading the same file
        event.target.value = '';
    }, [backgroundImages, onBackgroundImagesChange]);
    const handleImageSelection = useCallback((index) => {
        const updatedImages = backgroundImages.map((image, i) => ({
            ...image,
            isSelected: i === index,
            isBackgroundImage: i === index // Update background image property
        }));
        onBackgroundImagesChange?.(updatedImages);
    }, [backgroundImages, onBackgroundImagesChange]);
    const handleImageRemove = useCallback((index) => {
        const imageToRemove = backgroundImages[index];
        // Prevent removing if it's the only image left
        if (backgroundImages.length <= 1) {
            return;
        }
        const updatedImages = backgroundImages.filter((_, i) => i !== index);
        // If we're removing the selected image, select the first remaining image
        if (backgroundImages[index]?.isSelected && updatedImages.length > 0) {
            updatedImages[0].isSelected = true;
            updatedImages[0].isBackgroundImage = true;
        }
        onBackgroundImagesChange?.(updatedImages);
    }, [backgroundImages, onBackgroundImagesChange]);
    const selectedImage = backgroundImages.find((img) => img.isSelected);
    const imageUrl = selectedImage?.file
        ? imageUrls[`${selectedImage.file.name}-${selectedImage.file.size}-${selectedImage.file.lastModified}`]
        : selectedImage?.url;
    // Check if the selected image is a video
    const isVideoSelected = isVideo(selectedImage?.file?.type, selectedImage?.url);
    // Show uploaded images if any, otherwise show default thumbnails
    const displayImages = backgroundImages.length > 0 ? backgroundImages : [];
    return (_jsxs(_Fragment, { children: [validationErrors.length > 0 && (_jsx(Alert, { severity: 'error', sx: { mb: 2 }, children: validationErrors.map((error, index) => (_jsx("div", { children: error }, index))) })), _jsx(Box, { className: 'background-gallery', sx: { ...styles.backgroundAndPreview }, children: _jsxs(Box, { sx: { ...styles.innerContainer }, children: [_jsx(Typography, { variant: 'subtitle2', color: theme.palette.text.primary, children: t('common.startScreenPreview') }), _jsx(Typography, { variant: 'caption', color: theme.palette.text.secondary, children: t('common.startScreenDescription') }), _jsxs(Box, { sx: {
                                ...styles.previewMainContainer
                            }, children: [isVideoSelected ? (_jsx(Box, { component: 'video', src: imageUrl, autoPlay: true, muted: true, loop: true, playsInline: true, sx: {
                                        ...styles.previewImageContainer,
                                        objectFit: 'cover'
                                    } })) : (_jsx(Box, { component: 'img', src: imageUrl && imageUrl, alt: 'preview', sx: {
                                        ...styles.previewImageContainer
                                    } })), _jsx(Box, { sx: {
                                        ...styles.previewTextEditorContentContainer
                                    }, children: _jsx(TextEditor, { placeholder: String(t('events.createNewEvent.startScreenPlaceholder')), readOnly: true, value: resolvedWelcomeText, variant: 'white' }) })] })] }) }), _jsx(ThankYouScreenContent, { watch: watch, backgroundImages: backgroundImages })] }));
};
export default StartScreenContent;
